<template>
  <div id="donate-form" class="wwf-form donation-form">
    <!-- LOADING -->
    <div class="wwf-loading-container" v-show="donation.isLoading">
      <div class="wwf-loading">
        <span></span>
      </div>
    </div>

    <!-- Form -->
    <Stepper
      :thankYouUrl="thankYouUrl"
      :useProductThankYouUrl="useProductThankYouUrl"
      :blacklistUrl="blacklistUrl"
      @reset="reset"
    ></Stepper>
  </div>
</template>

<script>
import Stepper from "./Stepper.vue";
import {
  getParameterByName,
  getNameByDonationType,
  getNameByPaymentType,
  removeURLParameter,
} from "../../helpers";
import gtm from "../../utils/gtm";

import { mapState } from "vuex";

export default {
  name: "Donate",
  components: {
    Stepper,
  },

  props: {
    productId: {
      type: String,
      required: true,
    },
    thankYouUrl: {
      type: String,
      default: null,
    },
    useProductThankYouUrl: {
      type: Boolean,
      default: true,
    },
    blacklistUrl: {
      type: String,
    },
  },
  computed: mapState({
    donation: (state) => state.donation.donation,
    product: (state) => state.product.product,
  }),

  created: async function () {
    this.setLoading(true);

    this.checkPaypalCallback();

    // Fetch product informations
    // We can have query params to override the appeal and fund id
    await this.$store.dispatch("getProductById", {
      id: this.productId,
      source: window.location.href,
      appealid: getParameterByName("t"),
      fundid: getParameterByName("f"),
    });

    // Set default donation type by query param
    const dt = getParameterByName("dt");
    if (dt && (dt === "Single" || dt === "Regular")) {
      this.defaultDonationType = dt;
    }

    // DONATION TYPE - Pre-select the doallar handle and the package (rule: first item on the list)
    // As we only have Single and Regular, always set Single as the first option
    const defaultTypeExists =
      this.product.donationtypes.filter((e) => e === this.defaultDonationType)
        .length === 1;
    let defaultType =
      defaultTypeExists && this.defaultDonationType
        ? this.defaultDonationType
        : this.product.appealcodesetting.defaultdonationtype;

    // Reverse alphabetical order
    this.product.donationtypes = this.product.donationtypes.sort((a, b) => {
      if (a === defaultType) {
        return -1;
      }
      if (b === defaultType) {
        return 1;
      }
      return 0;
    });

    this.donation.type = defaultType;
    const settings = this.product.donationsettings[defaultType];
    if (settings) {
      this.donation.campaignid = settings.campaignid;
      this.donation.fundid = settings.fundid;
    }

    // DOLLAR HANDLE - Select first element
    const handles = this.product.dollarhandles[defaultType];
    if (handles) {
      this.donation.amount = handles[0].amount;
      this.donation.dollarhandle = handles[0].amount;
    }

    if (this.product.producttype != "wildcard") {
      // Pre-select the package (rule: first item on the list)
      this.donation.packagecode =
        this.product.packages && this.product.packages.length > 0
          ? this.product.packages[0]
          : null;
      await this.$store.dispatch("getAllPackagesForProduct");
    }

    // PURL: Check if there are some query string in URL to prefill the form, Query from donate widget
    const q = getParameterByName("q");
    if (q) {
      let paramsPrefill = "?" + window.atob(q);
      console.log("paramsPrefill", paramsPrefill);
      let amount = getParameterByName("amount", paramsPrefill);
      let email = getParameterByName("email", paramsPrefill);
      let firstname = getParameterByName("firstname", paramsPrefill);
      let lastname = getParameterByName("lastname", paramsPrefill);
      let salutation = getParameterByName("salutation", paramsPrefill);
      let wildcardCode = getParameterByName("wildcardCode", paramsPrefill);
      if (wildcardCode) {
        this.donation.packagecode = wildcardCode;
      }
      let dollarhandle = getParameterByName("dollarhandle", paramsPrefill)
        ? getParameterByName("dollarhandle", paramsPrefill).replace("$", "")
        : undefined;
      let dollarhandles = getParameterByName("dollarhandles", paramsPrefill);
      if (dollarhandles) {
        // get dollarhandles from query params
        dollarhandles = dollarhandles.replace(/[^0-9\. ]+/g, "").split(" ");
        let newDollarHandles = [];
        dollarhandles.forEach((v, index) => {
          // Check if there is a product dollar handles index
          if (this.product.dollarhandles.Single[index]) {
            newDollarHandles.push({
              amount: v,
              img: this.product.dollarhandles.Single[index].img,
              text: this.product.dollarhandles.Single[index].text,
            });
          }
        });
        this.product.dollarhandles.Single = newDollarHandles;
      }
      this.donation.dollarhandle = dollarhandle;
      this.donation.amount = amount;
      this.donation.email = email;
      this.donation.firstname = firstname;
      this.donation.lastname = lastname;
      this.donation.title = salutation;
    }

    // GTM: Push datalayer add to cart
    gtm.pushProductDetail(
      this.product.productname,
      this.product.id,
      this.product.producttype,
      getNameByDonationType(this.donation.type)
    );

    this.$store.dispatch("logProductVisit");

    this.setLoading(false);
  },
  methods: {
    async checkPaypalCallback() {
      try {
        // Check if there payment paypal pending to excecute
        const paymentId = getParameterByName("token");
        const payerId = getParameterByName("PayerID");
        if (payerId && paymentId) {
          console.log("Paypal Callback - Payment found");
          const donation = await this.$store.dispatch("paymentPaypal", {
            payerid: payerId,
            paymentid: paymentId,
          });
          this.donation.amount = donation.amount;
          console.log("Paypal Callback - Donaion", donation);
          this.$store.dispatch("logFormPaySuccess", donation.key);
          if (donation) {
            // Remove the query params from the URL
            let querylessUrl = removeURLParameter("token");
            querylessUrl = removeURLParameter("payerId", querylessUrl);
            window.history.pushState({}, document.title, querylessUrl);
            this.thanksUser(donation);
            this.reset();
          }
        }
      } catch (err) {
        console.error("Paypal Callback - error", err);
      }
    },
    thanksUser(donation) {
      this.$store.dispatch("logDonationComplete", donation);

      const donateThankYouUrl = donation.thankYouUrl;
      if (
        (this.useProductThankYouUrl && donateThankYouUrl) ||
        this.thankYouUrl
      ) {
        let query = `firstname=${donation.firstname}`;
        query += `&lastname=${donation.lastname}`;
        query += `&frequency=${donation.type}`;
        query += `&amount=${donation.amount}`;
        query += `&source=${document.URL}`;

        let params = "?q=" + window.btoa(query);

        const baseUrl =
          this.useProductThankYouUrl && donateThankYouUrl
            ? donateThankYouUrl
            : this.thankYouUrl;
        const url = baseUrl + params;

        // Send data to Google Tag Manager
        gtm.pushPurchase(
          donation.key,
          donation.productname,
          donation.productid,
          donation.producttype,
          getNameByDonationType(donation.type),
          donation.amount,
          donation.postcode,
          getNameByPaymentType("wwf_paypal"),
          function () {
            setTimeout(() => {
              window.open(url, "_self");
            }, 1000);
          }
        );
      } else {
        window.app.thankyou(
          donation.amount,
          donation.type,
          donation.key,
          donation.productname,
          donation.postcode,
          donation.firstname,
          donation.lastname,
          donation.producttype,
          donation.certificateurl,
          donation.productid,
          "wwf_paypal",
          donation.wildcard ? donation.wildcard.pdf : null
        );
      }
    },
    setLoading(value) {
      this.donation.isLoading = value;
    },
    async reset() {
      this.$store.dispatch("resetDonation");

      const defaultTypeExists =
        this.product.donationtypes.filter((e) => e === this.defaultDonationType)
          .length === 1;
      let defaultType =
        defaultTypeExists && this.defaultDonationType
          ? this.defaultDonationType
          : this.product.appealcodesetting.defaultdonationtype;

      this.donation.type = defaultType;
      const settings = this.product.donationsettings[defaultType];
      if (settings) {
        this.donation.campaignid = settings.campaignid;
        this.donation.fundid = settings.fundid;
      }

      if (this.product.producttype != "wildcard") {
        // Pre-select the package (rule: first item on the list)
        this.donation.packagecode =
          this.product.packages && this.product.packages.length > 0
            ? this.product.packages[0]
            : null;
        await this.$store.dispatch("getAllPackagesForProduct");
      }

      console.log("Reset donate form");
    },
  },
};
</script>



// WEBPACK FOOTER //
// Donate.vue?5f327364